import { convertHelpers } from 'global/common/convert';
import _ from 'lodash';
import { useCallback, useContext } from 'react';
import { SportsSeasonContext } from '../cnr/contexts/SportsSeasonContext';
import { sportsHelpers } from '../cnr/reducers/reducerHelpers/sportsHelpers';
import { _sportLevels, sortDB } from '../helpers/league';

export const useSportsFilters = () => {

  const sportsSeasonContext = useContext(SportsSeasonContext)
  const { sportsSeason_state } = sportsSeasonContext ?? {}

  const getLevelTeams = useCallback(({ teams }) => {
    return get_levelTeams(teams)
  }, []);

  const getHomeAwaysFromTeams = useCallback(({ teams }) => {
    const { levelTeams } = getLevelTeams({ teams })
    const homeAways = {}
    _.forEach(levelTeams, (level, levelKey) => {
      _.forEach(level, (section, sectionKey) => {
        const key = levelKey + '_' + sectionKey
        homeAways[key] = true
      })
    })
    return { homeAways }
  }, []);

  const getHomeAways = useCallback(({ levelTeams }) => {
    const homeAways = {}
    _.forEach(levelTeams, (level, levelKey) => {
      _.forEach(level, (section, sectionKey) => {
        const key = levelKey + '_' + sectionKey
        homeAways[key] = true
      })
    })
    return { homeAways }
  }, []);

  const getLevelMatches = useCallback(({ matches }) => {
    const levelMatches = get_LevelMatches(matches)
    return { levelMatches }
  }, []);

  const getSectionDates = useCallback(({ matches }) => {
    const levelMatches = get_LevelMatches(matches)
    const sectionDates = {}

    const _sectionDates = {}
    get_sectionDates(levelMatches, _sectionDates)

    _.forEach(levelMatches, (level) => {
      const _sections = _.groupBy(level, 'sections')
      _.forEach(_sections, (section) => {
        const uniqueStartDates = _.uniq(
          _.map(section, 'startDate')
        )
        const uniqueHomeTeams = _.uniq(
          _.map(section, item => _.get(item, 'home.name'))
        )
        sectionDates[_.size(uniqueHomeTeams)] = uniqueStartDates
      })
    })
    return { sectionDates, _sectionDates }
  }, []);

  const getTeamMatchGroups = useCallback((matches) => {
    const groupByTeam = () => {
      const groupedByTeam = {};
      _.forEach(matches, (match) => {
        const homeKey = match?.home?.id;
        const homeName = match?.home?.name;
        const awayKey = match?.away?.id;
        const awayName = match?.away?.name;

        if (homeKey) {
          if (!groupedByTeam[homeKey]) {
            groupedByTeam[homeKey] = {
              name: homeName,
              matches: []
            }
          }
          groupedByTeam[homeKey].matches.push(match);
        }

        if (awayKey) {
          if (!groupedByTeam[awayKey]) {
            groupedByTeam[awayKey] = {
              name: awayName,
              matches: []
            };
          }
          groupedByTeam[awayKey].matches.push(match);
        }
      });
      return groupedByTeam
    }
    return groupByTeam()
  })

  return { ...sportsSeason_fns(sportsSeason_state), getTeamMatchGroups, getSectionDates, getHomeAwaysFromTeams, getLevelTeams, getHomeAways, getLevelMatches, ...sportsHelpers }
}

const get_LevelMatches = (matches) => {
  const _allMatchCollections = _.groupBy(matches, '_matchCollectionKey')
  const levelMatches = _.omit(_allMatchCollections, [
    'nonLevel',
    'nonSection',
    'tournament'
  ])
  return levelMatches
}

const get_levelTeams = (teams, sectionCounts) => {

  const groupedTeams = _.reduce(
    teams,
    (result, team, teamKey) => {
      const { levels, sections } = team;

      // Skip teams without levels
      if (!levels) return result;

      // Ensure the structure for levels and sections
      if (!result[levels]) result[levels] = {};
      if (!result[levels][sections]) result[levels][sections] = {};

      // Add the team
      result[levels][sections][teamKey] = team;

      return result;
    },
    {}
  );

  const section_counts = _.mapValues(groupedTeams, (sections) => _.size(sections));

  const _sectionCounts = {}
  const levelTeams = {}

  _.forEach(_sportLevels, (sl) => {
    _sectionCounts[sl] = section_counts[sl] ? section_counts[sl] : 0
    levelTeams[sl] = {}
  })

  _.forEach(groupedTeams, (gt, lk) => {
    _.forEach(gt, (st, sk) => {
      _.forEach(st, (t, tk) => {
        convertHelpers.createNestedObject(levelTeams, [lk, sk, tk])
        levelTeams[lk][sk][tk] = {
          name: t.name,
          position: t.position,
          _itemKey: t._itemKey || tk
        }
      })
    })
  })

  const homeAways = {}
  _.forEach(levelTeams, (level, levelKey) => {
    _.forEach(level, (section, sectionKey) => {
      const key = levelKey + '_' + sectionKey
      homeAways[key] = true
    })
  })

  return { sectionCounts: _sectionCounts, levelTeams, homeAways }

}

const get_sectionDates = (levelMatches, sectionDates) => {
  const ONE_DAY = 24 * 60 * 60 * 1000

  _.forEach(levelMatches, (level) => {
    const _sections = _.groupBy(level, 'sections')

    _.forEach(_sections, (section) => {

      const normalizedStartDates = _.chain(section)
        .map('startDate')
        .map(d => {
          const time = new Date(d).setHours(0, 0, 0, 0)
          return isNaN(time) ? null : time
        })
        .compact() // removes null/undefined
        .sortBy()
        .reduce((acc, date) => {
          if (!acc.length) {
            acc.push([date])
          } else {
            const lastGroup = _.last(acc)
            const lastDate = _.last(lastGroup)

            if (date - lastDate <= ONE_DAY) {
              lastGroup.push(date)
            } else {
              acc.push([date])
            }
          }
          return acc
        }, [])
        .map(group => {
          const counts = _.countBy(group)
          const majorityDate = _.maxBy(
            Object.keys(counts),
            d => counts[d]
          )
          return new Date(Number(majorityDate))
            .toISOString()
            .split('T')[0]
        })
        .value()

      const uniqueHomeTeams = _.uniq(
        _.map(section, item => _.get(item, 'home.name'))
      )

      sectionDates[_.size(uniqueHomeTeams)] = normalizedStartDates
    })
  })

}

const sportsSeason_fns = (sportsSeason_state) => {

  const { seasonal_info } = sportsSeason_state ?? {}

  return {

    getTeamsAvailableByAllDates: () => {

      const dates_available = {}
      const { firstMatchDate, lastMatchDate } = seasonal_info

      const startDate = new Date(firstMatchDate);
      const endDate = new Date(lastMatchDate);

      // Create a loop to iterate through the dates
      const currentDate = new Date(startDate);

      while (currentDate <= endDate) {
        // Do something with currentDate
        const month = (currentDate.getMonth() + 1).toString().padStart(2, '0'); // Add 1 because months are zero-based
        const day = currentDate.getDate().toString().padStart(2, '0');
        const year = currentDate.getFullYear();
        const formattedDate = `${month}/${day}/${year}`

        const teams_available = {}

        const f = _.pickBy(matches, function (m) {
          if (m) {
            const _startDate = new Date(m.startDate);
            const _currentDate = new Date(currentDate);
            return _startDate.getDate() === _currentDate.getDate()
          }
        })

        if (f) {
          Object.keys(teams).forEach(teamKey => {
            const ta = _.pickBy(f, function (m) {
              if (m) { return m.teams.includes(teamKey) }
            })
            if (Object.keys(ta).length === 0) {
              teams_available[teamKey] = teams[teamKey]
            }
          })
          dates_available[formattedDate] = teams_available
        }

        // Increment currentDate by one day
        currentDate.setDate(currentDate.getDate() + 1);
      }
      return dates_available
    },

    sortSectionStandings: (teamz, playoffTeamsPerSection, sectionPlayoffTeams, sportsModeSport, forPlayoff, sortStandingsByPoints, levelKey) => sortDB.sortSectionStandings(teamz, playoffTeamsPerSection, sectionPlayoffTeams, sportsModeSport, forPlayoff, sortStandingsByPoints, levelKey),
    sortLevelStandings: (teamz) => sortDB.sortLevelStandings(teamz),

    updateTeamBests: (rankings, teamz) => {
      if (teamz && rankings) {
        Object.keys(teamz).forEach(teamKey => {
          const team = teamz[teamKey]
          team.weeklyRankings = {
            best: null,
            byWeek: []
          }
          const teamName = team.name
          const rankingsSorted = sortObj(rankings)
          Object.keys(rankingsSorted).forEach(weekKey => {
            const week = rankings[weekKey]
            Object.keys(week).forEach(levelKey => {
              if (team.levels === levelKey) {
                const weekLevelTeams = week[levelKey]
                const teamRank = getArrayIndex(weekLevelTeams, teamName)
                if (teamRank >= 0) {
                  const teamRankThisWeek = teamRank + 1
                  team.weeklyRankings.byWeek.push(teamRankThisWeek)
                  if (team.weeklyRankings.best) {
                    if (teamRankThisWeek < team.weeklyRankings.best) {
                      team.weeklyRankings.best = teamRankThisWeek
                    }
                  } else {
                    team.weeklyRankings.best = teamRankThisWeek
                  }
                } else {
                  team.weeklyRankings.byWeek.push(0)
                }
              }
            })
          })
        })
      }
    },
  }
}